import { useCallback, useEffect } from "react";
import { CircleUser, Cloud, GitBranch, Mail, Shield, ShieldCheck, Building2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { oauthStartUrl } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useIdentityConnections, type IdentityConnection } from "@/hooks/useIdentityConnections";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/page-header";
import { MemberApiKeysSection } from "@/pages/ApiKeysPage";
import { cn } from "@/lib/utils";
import { isPmoFeatureEnabled } from "@/lib/features";

type IdentityProvider = "atlassian" | "google";

function PersonalConnectionRow({
  provider,
  connected,
  disabled,
  disabledReason,
  onConnect,
}: {
  provider: IdentityProvider;
  connected: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onConnect: (provider: IdentityProvider) => void;
}) {
  const { t } = useTranslation();
  const Icon = provider === "google" ? Cloud : GitBranch;
  const name = t(`profile.personalConnections.${provider}.name`);
  const description = connected
    ? t(`profile.personalConnections.${provider}.connectedDescription`)
    : disabled && disabledReason
      ? disabledReason
      : t(`profile.personalConnections.${provider}.description`);

  return (
    <div className="flex items-center justify-between rounded-lg border bg-card px-4 py-3 transition-colors hover:bg-muted/40">
      <div className="flex items-center gap-3">
        <div className={cn(
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ring-1",
          connected
            ? "bg-primary/10 ring-primary/20 text-primary"
            : "bg-muted ring-border text-muted-foreground",
        )}>
          <Icon className="h-4 w-4" />
        </div>
        <div>
          <p className="text-sm font-medium">{name}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
      </div>
      {connected ? (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
          <span className="h-2 w-2 rounded-full bg-emerald-500" />
          {t("profile.personalConnections.connectedStatus")}
        </span>
      ) : (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onConnect(provider)}
          disabled={disabled}
        >
          {t("profile.personalConnections.connectButton")}
        </Button>
      )}
    </div>
  );
}

function PersonalConnectionsSection({
  connections,
  onConnect,
}: {
  connections: IdentityConnection[];
  onConnect: (provider: IdentityProvider) => void;
}) {
  const { t } = useTranslation();
  const connectedCount = connections.filter((c) => c.connected).length;

  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold">{t("profile.personalConnections.title")}</h3>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {t("profile.personalConnections.description")}
            </p>
          </div>
          {connectedCount > 0 && (
            <div className="flex items-center gap-1.5 rounded-full bg-background px-3 py-1 text-xs font-medium ring-1 ring-border">
              <ShieldCheck className="h-3 w-3 text-primary" />
              <span className="font-bold text-foreground">{connectedCount}</span>
              <span className="text-muted-foreground">{t("profile.personalConnections.connected")}</span>
            </div>
          )}
        </div>
      </div>
      <div className="space-y-2 p-4">
        {connections.map((conn) => (
            <PersonalConnectionRow
              key={conn.provider}
              provider={conn.provider}
              connected={conn.connected}
              onConnect={onConnect}
            />
        ))}
      </div>
    </div>
  );
}

export function ProfilePage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { connections, loading: connectionsLoading, refetch: refetchConnections } = useIdentityConnections();

  const handleConnect = useCallback(
    (provider: IdentityProvider) => {
      if (!user) return;
      const params = new URLSearchParams({
        user_key: user.email,
        redirect_uri: `${window.location.origin}/${user.org_slug}/profile`,
        // RAISE-15374: personal flow — tokens + account_id only, never
        // binds org.jira_site_url and never shows the org site picker.
        origin: "personal",
        // RAISE-15401: org_id removed — derived from auth context (session cookie).
      });
      window.location.href = oauthStartUrl(provider, params);
    },
    [user],
  );

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (!status) return;

    if (status === "success") {
      toast.success(t("profile.oauthStatus.success"));
      refetchConnections();
    } else if (status === "error") {
      const reason = params.get("reason") ?? "unknown";
      toast.error(t("profile.oauthStatus.error", { reason }));
    }
    // RAISE-15374: personal OAuth never returns site_selection_required —
    // the backend auto-completes with the first accessible site.

    window.history.replaceState({}, "", window.location.pathname);
  }, [t, refetchConnections]);

  return (
    <div className="space-y-6">
      <PageHeader icon={CircleUser} title={t("profile.title")} subtitle={t("profile.subtitle")} secondaryGlow={false} />

      {/* Personal info */}
      <Card>
        <CardContent className="pt-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted ring-1 ring-border shrink-0">
                <CircleUser className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("profile.fields.name")}</p>
                <p className="mt-0.5 text-sm font-medium">{user?.name ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted ring-1 ring-border shrink-0">
                <Mail className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("profile.fields.email")}</p>
                <p className="mt-0.5 text-sm font-medium">{user?.email ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted ring-1 ring-border shrink-0">
                <Shield className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("profile.fields.role")}</p>
                <p className="mt-0.5 text-sm font-medium capitalize">{user?.role ?? "—"}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-muted ring-1 ring-border shrink-0">
                <Building2 className="h-4 w-4 text-muted-foreground" />
              </div>
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{t("profile.fields.organization")}</p>
                <p className="mt-0.5 text-sm font-medium">{user?.org_name ?? "—"}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Personal connections */}
      {isPmoFeatureEnabled() && !connectionsLoading && connections.length > 0 && (
        <PersonalConnectionsSection
          connections={connections}
          onConnect={handleConnect}
        />
      )}

      {/* API Key section */}
      <MemberApiKeysSection />
    </div>
  );
}
